// LT-549 (LT-536): "Create campaign" splash — two visually distinct entry
// points for the two creation paths (LT-536 decision #3). The wizard card
// routes to the existing step-by-step CampaignWizardPage; the upload card
// (Campaign-from-Brief) is feature-flagged off until the extraction +
// review UI lands (LT-550/551/552). Path lock (decision #2) means the path
// chosen here is immutable for the campaign's life.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileUp, ListChecks, Workflow } from 'lucide-react';

import PageShell from '../../components/PageShell';
import { Button, Card, Pill } from '../../ui';
import { BRIEF_TEMPLATE_URL } from '../../constants/briefTemplate';

// LT-549 AC: the upload card links to a placeholder until the sibling
// extraction story (LT-550) + review UI (LT-551/552) land. Lane A flips
// this to `true` and wires `/campaigns/create/upload` when that ships.
// LT-551: extraction (LT-550) + the upload/clarification UI have landed —
// enabled.
const UPLOAD_PATH_ENABLED = true;
const UPLOAD_PATH_ROUTE = '/campaigns/create/upload';

const CampaignCreateSplashPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PageShell>
      <header className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/campaigns')}
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 mb-3"
        >
          <ArrowLeft size={14} strokeWidth={2} />
          Back to campaigns
        </button>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
          New campaign
        </div>
        <h1 className="text-2xl font-semibold text-slate-50">
          How do you want to build this campaign?
        </h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          Pick the path that matches how you work. Your choice is locked for
          this campaign — both paths produce the same kind of campaign, but
          edits later use the path you start with.
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 max-w-3xl">
        {/* Wizard path */}
        <Card
          elevation="raised"
          className="flex flex-col gap-4 cursor-pointer hover:border-accent/40 transition-colors"
          role="button"
          tabIndex={0}
          aria-label="Build step by step with the wizard"
          data-testid="create-path-wizard"
          onClick={() => navigate('/campaigns/new')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/campaigns/new');
            }
          }}
        >
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-ds-lg bg-accent-tint text-accent">
              <Workflow size={20} strokeWidth={2} />
            </span>
            <h2 className="text-lg font-semibold text-slate-50">
              Build step by step
            </h2>
          </div>
          <p className="text-sm text-slate-400 flex-1">
            The guided wizard walks you through audience, persona, pitch,
            objections and schedule one field at a time. Best when you know
            exactly what you want to say.
          </p>
          <Button intent="primary" className="self-start">
            <ListChecks size={16} strokeWidth={2.5} />
            Start the wizard
          </Button>
        </Card>

        {/* Upload path (Campaign-from-Brief) */}
        <Card
          elevation="raised"
          className={
            UPLOAD_PATH_ENABLED
              ? 'flex flex-col gap-4 cursor-pointer hover:border-accent/40 transition-colors'
              : 'flex flex-col gap-4 opacity-70'
          }
          role={UPLOAD_PATH_ENABLED ? 'button' : undefined}
          tabIndex={UPLOAD_PATH_ENABLED ? 0 : undefined}
          aria-label="Upload a brief"
          aria-disabled={UPLOAD_PATH_ENABLED ? undefined : true}
          data-testid="create-path-upload"
          onClick={UPLOAD_PATH_ENABLED ? () => navigate(UPLOAD_PATH_ROUTE) : undefined}
          onKeyDown={
            UPLOAD_PATH_ENABLED
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(UPLOAD_PATH_ROUTE);
                  }
                }
              : undefined
          }
        >
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-ds-lg bg-info-tint text-info-text">
              <FileUp size={20} strokeWidth={2} />
            </span>
            <h2 className="text-lg font-semibold text-slate-50 flex items-center gap-2">
              Upload a brief
              {UPLOAD_PATH_ENABLED ? null : <Pill intent="neutral">Coming soon</Pill>}
            </h2>
          </div>
          <p className="text-sm text-slate-400 flex-1">
            Drop in a one-page brief and let the assistant draft the campaign
            for you — it reads the brief, asks a few clarifying questions, and
            you review before launch. Best for agencies running campaigns for
            clients.
          </p>
          <div className="flex flex-col gap-2 items-start">
            <Button intent="secondary" disabled={!UPLOAD_PATH_ENABLED}>
              <FileUp size={16} strokeWidth={2.5} />
              {UPLOAD_PATH_ENABLED ? 'Upload a brief' : 'Coming soon'}
            </Button>
            {/* LT-545: link the downloadable brief template from the upload
                entry point so operators can get a head start now. */}
            <a
              href={BRIEF_TEMPLATE_URL}
              download
              className="text-xs text-accent hover:text-accent-hover hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Download the brief template (.docx)
            </a>
          </div>
        </Card>
      </div>
    </PageShell>
  );
};

export default CampaignCreateSplashPage;
